import { memo, useCallback, useEffect } from 'react';

import { AskExpertButton } from './AskExpertButton';
import { AskResultCard } from './AskResultCard';
import { AskTurnProgress } from './AskTurnProgress';
import { useAskConversationContext, useAskUiStore } from './LibraryChatContext';
import { ResearchTourZone } from './researchTutorial/ResearchTourZone';
import {
  useResearchTutorialActions,
  useResearchTutorialStore,
} from './researchTutorial/researchTutorialContext';
import { SELECTABLE_TEXT_PROPS } from './selectableText';
import { SEND_FAILURE_TITLE, sendFailureMessage } from './sendFailureMessages';

import { Badge, BadgeText } from '@/components/ui/badge';
import { Button, ButtonText } from '@/components/ui/button';
import { HStack } from '@/components/ui/hstack';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { useIsCompactLayout } from '@/hooks/useResponsiveLayout';
import { useToaster } from '@/hooks/useToaster';
import type { AskResult, AskTurn } from '@/services/ask/api';
import {
  isActiveTurn,
  isRepeatResult,
  isRetryableTurn,
  isUnfinishedTurn,
} from '@/services/ask/selectors';
import { selectSelectedResultIdForTurn } from '@/services/stores/askUiStore';

export const EXAMPLE_PROMPTS = [
  'EGFR exon 19 NSCLC progressing on osimertinib. Is amivantamab-lazertinib reasonable next?',
  "Frail patient with R/R myeloma after DRVd, ASCT and DR maintenance. What's next: bispecific or CAR-T?",
  'How should HER2-ultralow breast cancer be tested and reported for T-DXd eligibility?',
];

/**
 * Why a turn did not finish, by the snapshot's status and error code. A
 * timeout or a stall is worth retrying as is, so the copy invites it, but only
 * while Retry is offered: once something follows the turn, it just says what
 * happened.
 */
function unfinishedMessage(turn: AskTurn, canRetry: boolean): string {
  if (turn.status === 'stalled') {
    return canRetry ? "This search didn't finish. Try again." : "This search didn't finish.";
  }

  if (turn.errorCode === 'timeout') {
    return canRetry
      ? 'This search took too long and stopped. Try again in a moment.'
      : 'This search took too long and stopped.';
  }

  return canRetry
    ? 'Something went wrong while searching. Try again.'
    : 'Something went wrong while searching.';
}

function UserBubble({ text }: { text: string }) {
  return (
    <HStack className="w-full justify-end">
      <VStack className="max-w-[85%] rounded-2xl rounded-br-sm bg-primary-500 px-4 py-3">
        <Text className="text-base text-typography-white" {...SELECTABLE_TEXT_PROPS}>
          {text}
        </Text>
      </VStack>
    </HStack>
  );
}

/**
 * The reply side of the thread: the rewritten question, so it reads as
 * Research answering the reader rather than as a caption on the results. It
 * has no bubble fill, so it sits flush with the result cards beneath it on the
 * tinted page.
 */
function RewrittenQuestionBubble({ question }: { question: string }) {
  return (
    <HStack className="w-full justify-start">
      <VStack className="max-w-[85%] gap-1 py-3">
        <Text className="text-xs text-typography-700">Searched for</Text>

        <Text className="text-base text-typography-900" {...SELECTABLE_TEXT_PROPS}>
          {question}
        </Text>
      </VStack>
    </HStack>
  );
}

function resultSummary(results: readonly AskResult[]): string {
  const count = results.length;

  if (count === 0) {
    return 'No related answered inquiries found';
  }

  return `${count} ${count === 1 ? 'related inquiry' : 'related inquiries'}`;
}

/**
 * The reply to one question, drawn from the turn's status in the snapshot.
 * Running, finished and not-finished share one live region, so a screen reader
 * hears the outcome without the reader moving focus to find it.
 *
 * Only the side-by-side layout marks the result being viewed. On a phone an
 * opened result covers the thread, so a highlight left behind would only mark
 * a card the reader has already come back from.
 *
 * Retry is offered only on the conversation's last turn, and disabled while
 * any turn runs or a send is in flight: the server accepts one running turn
 * per conversation, and a retry queued behind another would only be refused.
 */
const AssistantTurn = memo(function AssistantTurn({
  turn,
  onSelectResult,
}: {
  turn: AskTurn;
  onSelectResult: (turnId: string, result: AskResult, position: number) => void;
}) {
  const { activeTurn, conversationId, isSending, retryTurn, snapshot } =
    useAskConversationContext();

  const selectedResultId = useAskUiStore(selectSelectedResultIdForTurn(turn.id));

  const isCompact = useIsCompactLayout();

  const toaster = useToaster();

  const tutorialConversationId = useResearchTutorialStore((state) => state.conversationId);

  const isTutorialTurn =
    tutorialConversationId === conversationId && snapshot?.turns[0]?.id === turn.id;

  const handleRetry = () => {
    retryTurn(turn)
      .then((error) => {
        if (error && error.kind !== 'NOT_FOUND') {
          toaster.showError({ title: SEND_FAILURE_TITLE, message: sendFailureMessage(error.kind) });
        }
      })
      .catch(() => {});
  };

  const handleSelect = useCallback(
    (result: AskResult, position: number) => onSelectResult(turn.id, result, position),
    [onSelectResult, turn.id]
  );

  if (isActiveTurn(turn)) {
    return (
      <AskTurnProgress
        stage={turn.stage}
        startedAt={turn.createdAt}
        turnId={turn.id}
        text={turn.text}
        isFollowUp={snapshot != null && snapshot.turns[0]?.id !== turn.id}
      />
    );
  }

  if (isUnfinishedTurn(turn)) {
    const isBusy = activeTurn != null || isSending;

    const canRetry = isRetryableTurn(snapshot, turn);

    return (
      <VStack
        className="gap-3 rounded-xl border border-error-500 bg-background-0 p-4"
        accessibilityLiveRegion="polite">
        <Text className="text-sm text-typography-900">{unfinishedMessage(turn, canRetry)}</Text>

        {canRetry ? (
          <Button
            size="sm"
            variant="outline"
            action="secondary"
            onPress={handleRetry}
            isDisabled={isBusy}
            accessibilityHint={isBusy ? 'Available when the current question finishes' : undefined}
            className="self-start">
            <ButtonText>Try again</ButtonText>
          </Button>
        ) : null}
      </VStack>
    );
  }

  const { results } = turn;

  /*
   * The thread offers its own handoff only on a turn that matched nothing,
   * where there are no answered inquiries to read first. Everywhere else the
   * "Start an inquiry" pill is the way to an expert.
   */
  const showsExpertButton = results.length === 0;

  /*
   * turn.text can be a rough note ("what next"); context.question is what the
   * search actually ran against and what an expert would see. Only worth a
   * caption when the two visibly diverge, so a clean input (an example
   * prompt, a well-formed question) doesn't get an unnecessary echo.
   */
  const rewrittenQuestion =
    turn.context?.question &&
    turn.context.question.trim().toLowerCase() !== turn.text.trim().toLowerCase()
      ? turn.context.question
      : null;

  return (
    <VStack className="gap-3">
      {rewrittenQuestion ? <RewrittenQuestionBubble question={rewrittenQuestion} /> : null}

      <ResearchTourZone segment="results" zone={1} active={isTutorialTurn}>
        <VStack className="gap-1" accessibilityLiveRegion="polite">
          <HStack className="flex-wrap items-center gap-2">
            <Text className="font-semibold text-sm text-typography-900">
              {resultSummary(results)}
            </Text>

            {turn.context?.contextChanged ? (
              <Badge action="info" size="sm">
                <BadgeText>New case</BadgeText>
              </Badge>
            ) : null}
          </HStack>

          {turn.context?.contextChanged ? (
            <Text className="text-xs text-typography-700">
              This question was treated as a new case, so it doesn't build on the ones above.
            </Text>
          ) : null}

          {results.length === 0 ? (
            <Text className="text-sm text-typography-700">
              Add detail in a follow-up, or ask a specialist directly.
            </Text>
          ) : null}
        </VStack>
      </ResearchTourZone>

      {results.map((result, position) => (
        <ResearchTourZone
          key={`${result.source}:${result.resultId}`}
          segment="results"
          zone={2}
          active={isTutorialTurn && position === 0}>
          <AskResultCard
            result={result}
            position={position}
            isRepeat={isRepeatResult(result, turn.id)}
            isSelected={!isCompact && selectedResultId === result.resultId}
            onSelect={handleSelect}
          />
        </ResearchTourZone>
      ))}

      {/*
        turn.text is only the reader's own words for this turn, which on a
        follow-up can be as short as "try again" — context.question is the
        server's recontextualized question, the same one the preview sheet
        shows, so both surfaces hand an expert the same case.
      */}
      {showsExpertButton ? (
        <AskExpertButton
          conversationId={conversationId}
          turnId={turn.id}
          questionText={turn.context?.question ?? turn.text}
          surface="thread"
        />
      ) : null}
    </VStack>
  );
});

export function LibraryChatTurnItem({
  turn,
  onSelectResult,
}: {
  turn: AskTurn;
  onSelectResult: (turnId: string, result: AskResult, position: number) => void;
}) {
  return (
    <VStack className="gap-4">
      <UserBubble text={turn.text} />

      <AssistantTurn turn={turn} onSelectResult={onSelectResult} />
    </VStack>
  );
}

export function LibraryChatEmptyState({
  onPickExample,
}: {
  onPickExample: (text: string) => void;
}) {
  const { requestStart, cancelStart } = useResearchTutorialActions();

  useEffect(() => {
    requestStart();

    return cancelStart;
  }, [requestStart, cancelStart]);

  return (
    <VStack className="w-full flex-1 gap-6 pb-10 pt-2 md:justify-center">
      <ResearchTourZone segment="example" zone={1}>
        <Text className="text-base text-typography-700">
          Describe a case or ask a question in your own words. We&apos;ll find answered inquiries
          from specialists that match, and you can ask follow-ups.
        </Text>
      </ResearchTourZone>

      <VStack className="gap-2">
        <Text className="font-semibold text-xs uppercase text-typography-700">Try an example</Text>

        {EXAMPLE_PROMPTS.map((prompt, index) => (
          <ResearchTourZone
            key={prompt}
            segment="example"
            zone={2}
            active={index === 0}
            action={() => onPickExample(prompt)}>
            <Pressable
              onPress={() => onPickExample(prompt)}
              accessibilityRole="button"
              accessibilityLabel={`Use example: ${prompt}`}
              className="rounded-xl border border-info-700 bg-secondary-50 p-3 web:cursor-pointer web:hover:bg-secondary-100">
              <Text className="text-sm text-typography-900">{prompt}</Text>
            </Pressable>
          </ResearchTourZone>
        ))}
      </VStack>
    </VStack>
  );
}
